// oncare-api 호출 클라이언트. ApiResponse 엔벨로프({success,data}/{success,error}) 처리.

import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "./token";
import type { AssessmentStatusCode } from "./assessment";

// CI 배포 빌드는 NEXT_PUBLIC_API_BASE(시크릿)로 주입한다. 미설정/빈값이면 로컬 개발 기본값.
const API_BASE =
  process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8080/api";

export type ApiError = { code: string; message: string };

type ApiEnvelope<T> =
  | { success: true; data: T; error: null }
  | { success: false; data: null; error: ApiError };

export class ApiException extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "ApiException";
    this.code = code;
    this.status = status;
  }
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  token?: string | null;
};

/** 단일 fetch + 엔벨로프 처리. accessToken 이 있으면 Authorization 헤더로 싣는다. */
async function send<T>(
  path: string,
  opts: { method?: string; body?: unknown; accessToken?: string | null } = {}
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      ...(opts.accessToken ? { Authorization: `Bearer ${opts.accessToken}` } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    cache: "no-store",
  });

  let envelope: ApiEnvelope<T> | null = null;
  try {
    envelope = (await res.json()) as ApiEnvelope<T>;
  } catch {
    // 본문 없음 / JSON 아님 (예: 시큐리티 필터의 빈 본문 403)
  }

  if (!res.ok || !envelope || !envelope.success) {
    const error = envelope && envelope.error
      ? envelope.error
      : { code: "UNKNOWN", message: `요청에 실패했습니다 (${res.status})` };
    throw new ApiException(error.code, error.message, res.status);
  }
  return envelope.data;
}

// 액세스 토큰 만료 시 단 한 번만 갱신하도록 진행 중 갱신을 공유한다(동시 요청 폭주 방지).
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const rt = getRefreshToken();
  if (!rt) return null;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const tokens = await send<TokenResponse>("/auth/staff/refresh", {
          method: "POST",
          body: { refreshToken: rt },
        });
        setTokens({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
        return tokens.accessToken;
      } catch {
        clearTokens(); // 리프레시 토큰도 만료/무효 → 세션 종료(로그인 화면으로 유도)
        return null;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

/**
 * 인증이 필요한 호출. 토큰은 항상 토큰 스토어에서 읽는다(자동 갱신 후에도 최신 유지).
 * 만료 등으로 401/403 이 오면 리프레시 토큰으로 한 번 갱신해 원요청을 자동 재시도한다.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const access = getAccessToken() ?? options.token ?? null;
  try {
    return await send<T>(path, { method: options.method, body: options.body, accessToken: access });
  } catch (err) {
    if (err instanceof ApiException && (err.status === 401 || err.status === 403)) {
      const fresh = await refreshAccessToken();
      if (fresh) {
        return await send<T>(path, { method: options.method, body: options.body, accessToken: fresh });
      }
    }
    throw err;
  }
}

// ---- 타입 (oncare-api DTO 대응) ----

export type TokenResponse = {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
};

export type Me = {
  type: "STAFF" | "PATIENT";
  id: number;
  name: string | null;
  role: string | null;
  grade: string | null;
};

export type Reservation = {
  id: number;
  status: string;
  patientId: number;
  patientName: string | null;
  therapistId: number | null;
  therapistName: string | null;
  requestedTherapistId: number | null;
  requestedTherapistName: string | null;
  sessionNo: number | null;
  modality: string | null;
  /** 룸 (백엔드 신규 필드; 미제공 시 클라이언트 결정론적 배정) */
  room?: string | null;
  scheduledAt: string | null;
  requestedDate: string | null;
  requestedTimeSlot: string | null;
  requestNote: string | null;
  createdAt: string | null;
};

export type ReservationStats = {
  scheduled: number;
  completed: number;
  pending: number;
  noShow: number;
};

export type BookReservationBody = {
  patientId: number;
  therapistId: number;
  scheduledAt: string;
  modality?: string | null;
};

/** 직원 확정 예약 일정 변경 — ReservationRescheduleRequest. therapistId 미지정 시 기존 담당 유지. */
export type RescheduleReservationBody = {
  scheduledAt: string;
  therapistId?: number | null;
};

export type Journal = {
  id: number;
  reservationId: number;
  authorId: number;
  /** 환자명 (백엔드 신규 필드) */
  patientName: string | null;
  /** 회차 (백엔드 신규 필드) */
  sessionNo: number | null;
  /** 작성 트레이너명 (백엔드 신규 필드) */
  authorName: string | null;
  /** 진단/부위 예: "무릎 ACL" (백엔드 신규 필드) */
  bodyPart: string | null;
  status: string;
  romFlexion: string | null;
  painNrs: number | null;
  muscleGrade: string | null;
  content: string | null;
  nextPlan: string | null;
  tags: string[];
  submittedAt: string | null;
  approvedAt: string | null;
  returnedAt: string | null;
  publishedAt: string | null;
  returnReason: string | null;
  returnComment: string | null;
  createdAt: string | null;
};

export type JournalStats = {
  pending: number;
  approvedToday: number;
  approvedThisWeek: number;
  returned: number;
};

export type Patient = {
  id: number;
  name: string;
  birthDate: string | null;
  sex: string | null;
  phone: string;
  guardianPhone: string | null;
  emergencyNote: string | null;
  status: string;
  createdAt: string | null;
};

export type PatientOverview = {
  id: number;
  name: string;
  phone: string;
  status: string;
  /** 성별 라벨 "남"/"여" (Patient.sex 매핑, 백엔드 신규 필드) */
  gender?: string | null;
  /** 만나이 (birthDate 기준, 백엔드 신규 필드) */
  age?: number | null;
  programName: string | null;
  programCategory: string | null;
  therapistName: string | null;
  completedSessions: number | null;
  totalSessions: number | null;
  lastVisitAt: string | null;
  createdAt: string | null;
};

export type ProgramOption = {
  id: number;
  name: string;
};

export type PatientCreateBody = {
  name: string;
  birthDate: string;
  phone: string;
  sex?: string | null;
  guardianPhone?: string | null;
  emergencyNote?: string | null;
  consentThirdParty?: boolean;
  consentMarketing?: boolean;
};

/** 환자 정보 수정 — null 이 아닌 필드만 반영(PatientUpdateRequest). */
export type PatientUpdateBody = {
  name?: string | null;
  birthDate?: string | null;
  phone?: string | null;
  sex?: string | null;
  guardianPhone?: string | null;
  emergencyNote?: string | null;
};

export type PatientConsentSummary = {
  patientId: number;
  patientName: string;
  phone: string;
  personalInfo: string | null;
  healthInfo: string | null;
  thirdParty: string | null;
  marketing: string | null;
  latestGrantedAt: string | null;
  nearestExpiresAt: string | null;
  overall: string;
};

export type Therapist = {
  id: number;
  userId: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
  licenseNo: string | null;
  employmentType: string | null;
  weeklyHours: number | null;
  baseSalary: number | null;
  status: string | null;
  programIds: number[];
};

export type TherapistCreateBody = {
  name: string;
  email: string;
  password: string;
  phone?: string | null;
  title?: string | null;
  licenseNo?: string | null;
  employmentType?: string | null;
  weeklyHours?: number | null;
  baseSalary?: number | null;
  programIds?: number[] | null;
};

/** 대시보드 집계 기간 — 쿼리 파라미터 값. 기본 month. */
export type DashboardPeriod = "today" | "week" | "month";

export type DashboardSummary = {
  /** 적용된 기간 라벨 문자열 (백엔드가 확정한 기간) */
  period: string;
  activePatients: number;
  todayReservations: number;
  submittedJournals: number;
  therapists: number;
  bookingRate: number | null;
  therapistUtilization: number | null;
  /** 선택 기간 매출(원). 필드명은 하위호환 위해 monthRevenue 유지. */
  monthRevenue: number;
  todayCompleted: number;
  presentTherapists: number;
  expiringConsents: number;
  activePatientsDelta: number | null;
  bookingRateDelta: number | null;
  utilizationDelta: number | null;
  /** 직전 동일기간 대비 매출 증감(원) */
  revenueDelta: number | null;
};

export type TherapistRosterRow = {
  therapistId: number;
  name: string | null;
  title: string | null;
  /** 주간 근무시간(시간). null = 근태 미기록 */
  weeklyHours: number | null;
  /** present|off|leave. null = 오늘 근태 없음 */
  attendanceStatus: string | null;
  /** 본인 오늘 예약수 */
  todayReservations: number;
};

export type TherapistRosterResponse = {
  /** 오늘 present 트레이너 수 */
  onDutyCount: number;
  /** 전체 트레이너 수 */
  totalCount: number;
  /** 오늘 전체 예약건수 (confirmed/in_progress/completed) */
  todayReservationTotal: number;
  rows: TherapistRosterRow[];
};

export type Payment = {
  id: number;
  patientId: number;
  patientName: string | null;
  amount: number;
  method: string;
  status: string;
  memo: string | null;
  paidAt: string | null;
  createdAt: string | null;
};

/** 결제 상태 enum 상수명 — PATCH /payments/{id}/status 의 status 값. */
export type PaymentStatusCode = "PAID" | "PENDING" | "REFUNDED";

export type PaymentCreateBody = {
  patientId: number;
  enrollmentId?: number | null;
  amount: number;
  method: string;
  status?: string;
  memo?: string | null;
};

export type SalesStats = {
  monthRevenue: number;
  paymentCount: number;
  payingPatients: number;
  avgPerPatient: number;
  unpaid: number;
};

export type RevenueTrendPoint = {
  month: string;
  revenue: number;
  /** 월 예약수 (백엔드 신규 필드) */
  reservationCount?: number | null;
  /** 월 객단가 = 매출/예약수 (백엔드 신규 필드; 미제공 시 클라이언트 산출) */
  avgTicket?: number | null;
};

export type TreatmentMixPoint = { label: string; count: number };

export type TherapistOperation = {
  therapistId: number;
  name: string | null;
  title: string | null;
  status: string | null;
  assignedPatients: number;
  utilization: number | null;
  /** 이번주 예약수 (백엔드 신규 필드; 미제공 시 클라이언트 추정) */
  weeklyBookings?: number | null;
  /** 이번주 매출 (백엔드 신규 필드; 미제공 시 추정 = 예약수 × 평균 객단가) */
  weeklyRevenue?: number | null;
};

/** 트레이너 휴가(관리자 승인 대상). type=annual|sick|etc, status=requested|approved|rejected. */
export type Leave = {
  id: number;
  therapistId: number;
  therapistName: string | null;
  type: string | null;
  /** YYYY-MM-DD */
  startDate: string | null;
  endDate: string | null;
  status: string | null;
  reason: string | null;
};

/** 트레이너 정산(월별). status=pending|completed, 금액은 원 단위. */
export type Settlement = {
  id: number;
  therapistId: number;
  therapistName: string | null;
  /** YYYY-MM */
  period: string;
  status: string | null;
  scheduledPayAt: string | null;
  baseSalary: number | null;
  sessionCount: number | null;
  sessionPay: number | null;
  deduction: number | null;
  total: number | null;
  settledAt: string | null;
};

export type StaffUser = {
  id: number;
  email: string;
  name: string;
  role: string | null;
  grade: string | null;
  status: string | null;
  phone: string | null;
};

export type StaffUserCreateBody = {
  name: string;
  email: string;
  password: string;
  role: string;
  grade: string;
  phone?: string | null;
};

export type ClinicInfo = {
  name: string | null;
  address: string | null;
  representative: string | null;
  phone: string | null;
  businessNo: string | null;
  operatingHours: string | null;
};

// 평가지·자료 공유
export type Assessment = {
  id: number;
  patientId: number;
  patientName: string;
  /** 유형 한글 라벨 (예: "초기 평가") */
  type: string;
  /** 상태 한글 라벨 (예: "작성 중") */
  status: string;
  therapistName: string | null;
  createdAt: string | null;
};

/** 평가지 단일 측정 항목. value 는 자유 입력, unit 은 표시 단위(°/0-10/초/점/text 등). */
export type AssessmentItem = {
  category: string | null;
  label: string;
  value: string | null;
  unit: string | null;
};

/** category 단위로 묶인 측정 항목 그룹. */
export type AssessmentCategoryGroup = {
  category: string;
  items: AssessmentItem[];
};

/** 평가지 상세(전체 내용) — '보기' 모달용. 헤더 + 메모 + category 그룹 항목. */
export type AssessmentDetail = {
  id: number;
  patientName: string | null;
  /** 유형 한글 라벨 */
  type: string;
  /** 상태 한글 라벨 */
  status: string;
  therapistName: string | null;
  createdAt: string | null;
  memo: string | null;
  categories: AssessmentCategoryGroup[];
};

export type AssessmentTemplate = {
  id: number;
  name: string;
  description: string | null;
  itemCount: number;
  /** 유형 한글 라벨 */
  type: string;
  deployed: boolean;
  createdAt: string | null;
};

/** 평가지 양식 유형 — 입력은 상수코드. */
export type AssessmentTypeCode = "INITIAL" | "REASSESS" | "PAIN_VAS";

export type TemplateCreateBody = {
  name: string;
  description?: string;
  itemCount: number;
  type: AssessmentTypeCode;
};

export type MaterialShareBody = {
  title: string;
  category?: string | null;
  description?: string | null;
  linkUrl?: string | null;
};

export type Material = {
  id: number;
  title: string;
  category: string | null;
  description: string | null;
  linkUrl: string | null;
  /** 공유한 직원 이름 (백엔드 보강 필드) */
  sharedByName: string | null;
  createdAt: string | null;
};

// 공지사항 — 대상(audience)은 enum 상수명.
export type NoticeAudience = "ALL" | "STAFF" | "PATIENT";

export type Notice = {
  id: number;
  title: string;
  body: string;
  audience: NoticeAudience;
  pinned: boolean;
  publishedAt: string | null;
};

export type NoticeCreateBody = {
  title: string;
  content: string;
  audience: NoticeAudience;
  pinned: boolean;
};

export type NoticeUpdateBody = NoticeCreateBody;

export const api = {
  staffLogin: (email: string, password: string) =>
    send<TokenResponse>("/auth/staff/login", {
      method: "POST",
      body: { email, password },
    }),

  me: (token: string) => request<Me>("/auth/me", { token }),

  reservationSchedule: (token: string, range?: string) =>
    request<Reservation[]>(`/reservations/schedule${range ? `?range=${range}` : ""}`, { token }),

  reservationStats: (token: string, range?: string) =>
    request<ReservationStats>(`/reservations/stats${range ? `?range=${range}` : ""}`, { token }),

  bookReservation: (token: string, body: BookReservationBody) =>
    request<Reservation>("/reservations", { method: "POST", token, body }),

  completeReservation: (token: string, id: number) =>
    request<Reservation>(`/reservations/${id}/complete`, { method: "POST", token }),

  noShowReservation: (token: string, id: number) =>
    request<Reservation>(`/reservations/${id}/no-show`, { method: "POST", token }),

  /** 확정 예약 일정/담당 변경 — PATCH /reservations/{id}/reschedule. */
  rescheduleReservation: (token: string, id: number, body: RescheduleReservationBody) =>
    request<Reservation>(`/reservations/${id}/reschedule`, { method: "PATCH", token, body }),

  // 재활 일지 (관리자 승인)
  submittedJournals: (token: string) =>
    request<Journal[]>("/journals/submitted", { token }),

  journalStats: (token: string) => request<JournalStats>("/journals/stats", { token }),

  listJournals: (token: string, status: string) =>
    request<Journal[]>(`/journals?status=${encodeURIComponent(status)}`, { token }),

  approveJournal: (token: string, id: number) =>
    request<Journal>(`/journals/${id}/approve`, { method: "POST", token }),

  returnJournal: (token: string, id: number, reason?: string, comment?: string) =>
    request<Journal>(`/journals/${id}/return`, {
      method: "POST",
      token,
      body: { reason: reason ?? null, comment: comment ?? null },
    }),

  // 환자 관리
  listPatients: (token: string, status?: string, q?: string) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    const qs = params.toString();
    return request<Patient[]>(`/patients${qs ? `?${qs}` : ""}`, { token });
  },

  patientOverview: (token: string, q?: string) =>
    request<PatientOverview[]>(`/patients/overview${q ? `?q=${encodeURIComponent(q)}` : ""}`, { token }),

  createPatient: (token: string, body: PatientCreateBody) =>
    request<Patient>("/patients", { method: "POST", token, body }),

  updatePatient: (token: string, id: number, body: PatientUpdateBody) =>
    request<Patient>(`/patients/${id}`, { method: "PUT", token, body }),

  dischargePatient: (token: string, id: number, reason?: string, transferTherapistId?: number) =>
    request<Patient>(`/patients/${id}/discharge`, {
      method: "POST",
      token,
      body: { reason: reason ?? null, transferTherapistId: transferTherapistId ?? null },
    }),

  // 트레이너 운영
  listTherapists: (token: string) => request<Therapist[]>("/therapists", { token }),

  // 프로그램 옵션 (트레이너 담당 프로그램 멀티선택)
  programOptions: (token: string) => request<ProgramOption[]>("/programs/options", { token }),

  createTherapist: (token: string, body: TherapistCreateBody) =>
    request<Therapist>("/therapists", { method: "POST", token, body }),

  updateTherapist: (token: string, id: number, body: Partial<TherapistCreateBody> & { status?: string }) =>
    request<Therapist>(`/therapists/${id}`, { method: "PUT", token, body }),

  // 트레이너 운영 - 휴가 승인
  pendingLeaves: (token: string) =>
    request<Leave[]>("/leaves?status=requested", { token }),

  approveLeave: (token: string, id: number) =>
    request<Leave>(`/leaves/${id}/approve`, { method: "POST", token }),

  rejectLeave: (token: string, id: number) =>
    request<Leave>(`/leaves/${id}/reject`, { method: "POST", token }),

  // 트레이너 운영 - 정산 관리
  listSettlements: (token: string, month?: string) =>
    request<Settlement[]>(`/settlements${month ? `?month=${month}` : ""}`, { token }),

  confirmSettlement: (token: string, id: number) =>
    request<Settlement>(`/settlements/${id}/confirm`, { method: "POST", token }),

  // 대시보드
  dashboardSummary: (token: string, period?: DashboardPeriod) =>
    request<DashboardSummary>(
      `/dashboard/summary${period ? `?period=${period}` : ""}`,
      { token },
    ),

  therapistRoster: (token: string) =>
    request<TherapistRosterResponse>("/dashboard/therapist-roster", { token }),

  therapistOperations: (token: string) =>
    request<TherapistOperation[]>("/dashboard/therapist-operations", { token }),

  revenueTrend: (token: string) =>
    request<RevenueTrendPoint[]>("/dashboard/revenue-trend", { token }),

  treatmentMix: (token: string) =>
    request<TreatmentMixPoint[]>("/dashboard/treatment-mix", { token }),

  // 매출
  salesStats: (token: string, month?: string) =>
    request<SalesStats>(`/payments/stats${month ? `?month=${month}` : ""}`, { token }),

  listPayments: (token: string, month?: string) =>
    request<Payment[]>(`/payments${month ? `?month=${month}` : ""}`, { token }),

  createPayment: (token: string, body: PaymentCreateBody) =>
    request<Payment>("/payments", { method: "POST", token, body }),

  /** 결제 상태 변경(미수→수납, 수납→환불). status 는 enum 상수명(PAID/REFUNDED). */
  updatePaymentStatus: (token: string, id: number, status: PaymentStatusCode) =>
    request<Payment>(`/payments/${id}/status`, {
      method: "PATCH",
      token,
      body: { status },
    }),

  /** 결제 삭제(오등록 정정) — 미수(pending) 건만 삭제 가능. */
  deletePayment: (token: string, id: number) =>
    request<void>(`/payments/${id}`, { method: "DELETE", token }),

  // 개인정보 동의
  listConsents: (token: string, status?: string) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<PatientConsentSummary[]>(`/consents${qs}`, { token });
  },

  withdrawConsent: (token: string, patientId: number, item: string) =>
    request<void>(`/consents/${patientId}/withdraw`, { method: "POST", token, body: { item } }),

  sendConsentRenewRequests: (token: string, patientIds: number[], note?: string) =>
    request<{ sent: number }>(`/consents/renew-requests`, {
      method: "POST",
      token,
      body: { patientIds, note },
    }),

  // 설정 - 직원·권한
  listUsers: (token: string) => request<StaffUser[]>("/users", { token }),

  createUser: (token: string, body: StaffUserCreateBody) =>
    request<StaffUser>("/users", { method: "POST", token, body }),

  updateUser: (token: string, id: number, body: Partial<StaffUserCreateBody> & { status?: string }) =>
    request<StaffUser>(`/users/${id}`, { method: "PUT", token, body }),

  // 설정 - 클리닉 정보
  getClinic: (token: string) => request<ClinicInfo | null>("/clinic", { token }),

  updateClinic: (token: string, body: ClinicInfo) =>
    request<ClinicInfo>("/clinic", { method: "PUT", token, body }),

  // 평가지·자료 공유
  listAssessments: (token: string) => request<Assessment[]>("/assessments", { token }),

  /** 평가지 상세(전체 내용) — 측정 항목(category 그룹) + 메모. */
  getAssessment: (token: string, id: number) =>
    request<AssessmentDetail>(`/assessments/${id}`, { token }),

  /** 평가지 작성 상태 변경(DRAFT→REVIEW→DONE→SHARED). status 는 enum 상수명. */
  updateAssessmentStatus: (token: string, id: number, status: AssessmentStatusCode) =>
    request<void>(`/assessments/${id}/status`, {
      method: "PATCH",
      token,
      body: { status },
    }),

  listAssessmentTemplates: (token: string) =>
    request<AssessmentTemplate[]>("/assessment-templates", { token }),

  createAssessmentTemplate: (token: string, body: TemplateCreateBody) =>
    request<AssessmentTemplate>("/assessment-templates", { method: "POST", token, body }),

  /** 평가지 양식 메타데이터 수정(이름·설명·문항수·유형 — 문항 편집 스키마 제외). */
  updateAssessmentTemplate: (token: string, id: number, body: TemplateCreateBody) =>
    request<AssessmentTemplate>(`/assessment-templates/${id}`, { method: "PUT", token, body }),

  deployAssessmentTemplate: (token: string, id: number, deployed: boolean) =>
    request<AssessmentTemplate>(`/assessment-templates/${id}/deploy`, {
      method: "PATCH",
      token,
      body: { deployed },
    }),

  deleteAssessmentTemplate: (token: string, id: number) =>
    request<void>(`/assessment-templates/${id}`, { method: "DELETE", token }),

  /** 해당 환자에게 공유된 자료 목록(최신순). */
  listPatientMaterials: (token: string, patientId: number) =>
    request<Material[]>(`/patients/${patientId}/materials`, { token }),

  sharePatientMaterial: (token: string, patientId: number, body: MaterialShareBody) =>
    request<Material>(`/patients/${patientId}/materials`, { method: "POST", token, body }),

  /** 공유자료 메타데이터 수정(제목·분류·설명·링크). */
  updateMaterial: (token: string, patientId: number, materialId: number, body: MaterialShareBody) =>
    request<Material>(`/patients/${patientId}/materials/${materialId}`, { method: "PUT", token, body }),

  /** 공유 취소 — 해당 환자에게 공유된 자료 삭제. */
  deletePatientMaterial: (token: string, patientId: number, materialId: number) =>
    request<void>(`/patients/${patientId}/materials/${materialId}`, { method: "DELETE", token }),

  // 공지사항
  listNotices: (token: string) => request<Notice[]>("/notices", { token }),

  /** 관리자: 대상 무관 전체 공지(관리 목록 — 환자 전용 포함). */
  listAllNotices: (token: string) => request<Notice[]>("/notices/all", { token }),

  createNotice: (token: string, body: NoticeCreateBody) =>
    request<Notice>("/notices", { method: "POST", token, body }),

  updateNotice: (token: string, id: number, body: NoticeUpdateBody) =>
    request<Notice>(`/notices/${id}`, { method: "PUT", token, body }),

  deleteNotice: (token: string, id: number) =>
    request<void>(`/notices/${id}`, { method: "DELETE", token }),
};
